"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { LuMenu, LuSearch } from "react-icons/lu";

import { DesktopNavigation } from "@/components/app/app-layout/layout-navigation-desktop";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { focusIOSKeyboardBridge } from "@/components/app/editor-v3/editor-dom-focus";
import { IsLoggedIn } from "@/components/shared/is-logged-in";
import { MenuAttentionIndicator } from "@/components/shared/menu-attention-indicator";
import { SignUp } from "@/components/shared/signup";
import { StickyStack } from "@/components/shared/sticky-stack";
import { ThemeLogo } from "@/components/shared/theme-logo";
import { ThemeToggle } from "@/components/shared/theme-toggle";
import { UserDropdown } from "@/components/shared/user-dropdown";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useLayout } from "@/contexts/layout-context";
import { useBackgroundPathname } from "@/hooks/use-background-pathname";
import { useHistoryManager } from "@/hooks/use-history-manager";
import { useMobile } from "@/hooks/use-mobile";
import { APP_ROUTES, LARGE_WIDTH_BREAKPOINT } from "@/utils/constants";
import { DIALOG } from "@/utils/dialog-routes";

const AppLayoutTopNavigation = () => {
  const { hideTopNav, safeInsetTop } = useLayout();
  const { isDevice } = useMobile(LARGE_WIDTH_BREAKPOINT);

  if (hideTopNav || !isDevice) {
    return null;
  }

  return (
    <StickyStack
      offset={safeInsetTop * -1}
      className="
        unforce-radius z-50 flex w-full items-center justify-between bg-background/95 px-2 pb-2 shadow-[0_1px_0_var(--border)]
        lg:hidden
      "
      style={{
        paddingTop: `calc(0.5rem + ${safeInsetTop}px)`,
        marginTop: safeInsetTop * -1,
      }}
    >
      <div className="flex w-full items-center gap-2">
        <MobileDrawerNavigation />
        <Link href={APP_ROUTES.app} className="w-[60px]" prefetch={false}>
          <ThemeLogo width={60} />
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <IsLoggedIn>
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 rounded-full border-border"
              onClick={() =>
                setDialog(DIALOG.Search, {
                  open: true,
                })
              }
            >
              <LuSearch className="size-[1.2rem]" />
            </Button>

            <UserDropdown avatarSize={40} showUsername={false} />
          </IsLoggedIn>
          <IsLoggedIn guestOnly>
            <SignUp
              showBountyHunterLink={false}
              showTradeFocusLink={false}
              innerWrapperClassName="py-4"
            />
          </IsLoggedIn>
        </div>
      </div>
    </StickyStack>
  );
};

const MobileDrawerNavigation = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = useBackgroundPathname();
  const previousPathnameRef = useRef(pathname);
  const skipNextFinalFocusRef = useRef(false);
  const [openedAtPathname, setOpenedAtPathname] = useState(pathname);
  const open = drawerOpen && openedAtPathname === pathname;
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setDrawerOpen(nextOpen);

      if (nextOpen) {
        setOpenedAtPathname(pathname);
        skipNextFinalFocusRef.current = false;
      }
    },
    [pathname],
  );
  const preparePostDialogOpen = useCallback(() => {
    skipNextFinalFocusRef.current = focusIOSKeyboardBridge();
  }, []);
  const history = useHistoryManager({
    instanceId: "mobile-navigation",
    kind: "drawer",
    name: "mobile-navigation",
    onOpenChange: handleOpenChange,
    open,
    replace: true,
  });
  const closeHistoryEntry = history.close;
  const dismissHistoryEntry = history.dismiss;

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return;
    }

    previousPathnameRef.current = pathname;

    if (drawerOpen) {
      dismissHistoryEntry();
    }
  }, [dismissHistoryEntry, drawerOpen, pathname]);

  return (
    <Sheet open={open} onOpenChange={history.onOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open navigation menu"
          className="relative shrink-0 rounded-full text-muted"
        >
          <LuMenu className="size-[1.35rem]" />
          <MenuAttentionIndicator
            attentionKey="subscription-tier-health"
            animationKey="mobile-hamburger:subscription-tier-health"
            className="absolute top-[-3px] right-[-3px]"
          />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className="w-auto overflow-auto"
        finalFocus={() => {
          if (!skipNextFinalFocusRef.current) {
            return true;
          }

          skipNextFinalFocusRef.current = false;
          return false;
        }}
      >
        <SheetTitle className="hidden" />
        <SheetDescription className="hidden" />
        <div className="w-left-sidebar">
          <div className="mb-4 flex items-center gap-4">
            <Link href={APP_ROUTES.app} className="block" prefetch={false}>
              <ThemeLogo width={50} />
            </Link>
            <ThemeToggle align="start" />
          </div>
          <DesktopNavigation
            closeDrawer={closeHistoryEntry}
            dismissDrawer={dismissHistoryEntry}
            preparePostDialogOpen={preparePostDialogOpen}
            replaceLinks={open}
            setDrawerOpen={setDrawerOpen}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AppLayoutTopNavigation;
